import React from 'react';
import DiffMatchPatch from 'diff-match-patch';
import LoadingSpinner from './LoadingSpinner';
import './AIImprovementModal.css';

interface AIImprovementModalProps {
  isOpen: boolean;
  isLoading: boolean;
  originalText: string;
  improvedText: string | null;
  error: string | null;
  usageInfo?: {
    tokens_used: number;
    estimated_cost: number;
    response_time_ms: number;
    remaining_requests: number;
  };
  onAccept: () => void;
  onCancel: () => void;
}

const AIImprovementModal: React.FC<AIImprovementModalProps> = ({
  isOpen,
  isLoading,
  originalText,
  improvedText,
  error,
  usageInfo,
  onAccept,
  onCancel
}) => {
  if (!isOpen) return null;

  // Generate diff HTML using diff-match-patch
  const generateDiffHtml = (): string => {
    if (!improvedText) return '';

    const dmp = new DiffMatchPatch();
    const diffs = dmp.diff_main(originalText, improvedText);
    dmp.diff_cleanupSemantic(diffs);

    let html = '';
    for (const [operation, text] of diffs) {
      const escapedText = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>');

      if (operation === DiffMatchPatch.DIFF_DELETE) {
        // Deleted text - show in red with strikethrough
        html += `<span class="diff-delete">${escapedText}</span>`;
      } else if (operation === DiffMatchPatch.DIFF_INSERT) {
        // Inserted text - show in green with highlight
        html += `<span class="diff-insert">${escapedText}</span>`;
      } else {
        // Unchanged text
        html += `<span class="diff-equal">${escapedText}</span>`;
      }
    }

    return html;
  };

  return (
    <div className="ai-improvement-modal-overlay" onClick={onCancel}>
      <div className="ai-improvement-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ai-improvement-modal-header">
          <div>
            <h2>AI Paragraph Improvement</h2>
            <p className="modal-subtitle">Review the suggested changes</p>
          </div>
          <button
            onClick={onCancel}
            className="modal-close-btn"
            title="Close"
            disabled={isLoading}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="ai-improvement-modal-content">
          {isLoading ? (
            <div className="loading-state">
              <LoadingSpinner size="lg" />
              <p className="loading-text">Improving your paragraph...</p>
              <p className="loading-subtext">This may take a few seconds</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <div className="error-icon">❌</div>
              <h3>Improvement Failed</h3>
              <p className="error-message">{error}</p>
              <button onClick={onCancel} className="btn-secondary">
                Close
              </button>
            </div>
          ) : improvedText ? (
            <>
              {/* Diff View */}
              <div className="diff-section">
                <div className="diff-header">
                  <h3>Changes Preview</h3>
                  <div className="diff-legend">
                    <span className="legend-item">
                      <span className="legend-color legend-delete"></span>
                      <span className="legend-label">Removed</span>
                    </span>
                    <span className="legend-item">
                      <span className="legend-color legend-insert"></span>
                      <span className="legend-label">Added</span>
                    </span>
                  </div>
                </div>
                <div
                  className="diff-content"
                  dangerouslySetInnerHTML={{ __html: generateDiffHtml() }}
                />
              </div>

              {/* Original vs Improved Comparison */}
              <div className="comparison-section">
                <div className="comparison-column">
                  <h4>Original</h4>
                  <div className="text-preview">
                    {originalText}
                  </div>
                </div>
                <div className="comparison-column">
                  <h4>Improved</h4>
                  <div className="text-preview">
                    {improvedText}
                  </div>
                </div>
              </div>

            </>
          ) : null}
        </div>

        {/* Footer */}
        {!isLoading && !error && improvedText && (
          <div className="ai-improvement-modal-footer">
            <button onClick={onCancel} className="btn-cancel">
              Cancel
            </button>
            <button onClick={onAccept} className="btn-accept">
              ✓ Accept Changes
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIImprovementModal;
